/** @jsxImportSource @emotion/react */
import type { OperationsScheduleNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import DataGrid from 'src/components/ts-components/DataGrid'
import {
  OPERATIONS_GRID_HEADER_HEIGHT,
  OPERATIONS_GRID_ROW_HEIGHT,
  operationsGridStyle,
} from '../operationsGridStyle'
import {
  useSeasonDivisionsColumns,
  type DivisionRowAction,
  type SeasonDivisionRow,
} from '../../grid/useSeasonDivisionsColumns'
import { useUpdateDivisionField } from '../../hooks/useUpdateDivisionField'
import { shouldIgnoreCellClick } from '../../utils/isActionsCellClick'

interface SeasonDivisionsGridProps {
  /** The node these rows belong to — names the columns. */
  parent: OperationsScheduleNode
  divisions: OperationsScheduleNode[]
  rootScheduleId: string
  canEdit: boolean
  onSelectDivision: (nodeId: string) => void
  onRowAction: (action: DivisionRowAction, row: SeasonDivisionRow) => void
}

/**
 * The season's (or conference's) child divisions as an editable sheet. Both
 * node types list child schedules, so they share this one grid.
 *
 * Deliberately NOT drag-and-drop: divisions and conferences are reordered
 * through the inline Order cell, which writes `sort_order` directly. Only teams
 * drag — see `DivisionTeamsGrid`.
 */
const SeasonDivisionsGrid = ({
  parent,
  divisions,
  rootScheduleId,
  canEdit,
  onSelectDivision,
  onRowAction,
}: SeasonDivisionsGridProps) => {
  'use memo'

  const { updateField, pendingIds } = useUpdateDivisionField(rootScheduleId)
  const columns = useSeasonDivisionsColumns({
    canEdit,
    parent,
    onEditField: updateField,
    onRowAction,
  })

  const rows: SeasonDivisionRow[] = divisions.map((division) => ({
    ...division,
    pending: pendingIds.has(division.scheduleId),
    // ALL children, not just teams: a conference row must count its divisions.
    childCount: division.children.length,
  }))

  return (
    <div css={operationsGridStyle}>
      <DataGrid<SeasonDivisionRow>
        rowData={rows}
        columnDefs={columns}
        getRowId={({ data }) => data.id}
        domLayout='autoHeight'
        headerHeight={OPERATIONS_GRID_HEADER_HEIGHT}
        rowHeight={OPERATIONS_GRID_ROW_HEIGHT}
        rowClassRules={{
          'operations-row-pending': ({ data }) => Boolean(data?.pending),
        }}
        // Selection is driven from CELL clicks, not row clicks: only the cell
        // event carries `colDef`, and an editable cell must be left to enter
        // edit mode. Selecting re-renders the grid, which would tear the cell
        // back out before it could take focus. (The roster grids sidestep this
        // by having no row click at all — see RosterGrid.)
        onCellClicked={(event) => {
          if (
            event.data &&
            !shouldIgnoreCellClick(event.colDef, event.data, event.event)
          ) {
            onSelectDivision(event.data.id)
          }
        }}
        data-testid='season-divisions-grid'
      />
    </div>
  )
}

export default SeasonDivisionsGrid
